"use client";

import { Core } from "@faire/design-tokens";
import { FilterName } from "@faire/retailer-visitor-shared/components/Filters/FilterName";
import { MaybeDraggableStickyRowForLoggedOutPage } from "@faire/retailer-visitor-shared/components/Filters/MaybeDraggableStickyRowForLoggedOutPage";
import {
  Header,
  Sort,
} from "@faire/retailer-visitor-shared/components/Filters/Sort";
import { ToggleFilterRow } from "@faire/retailer-visitor-shared/components/Filters/ToggleFilterRow";
import { ToggleSidebarFiltersPill } from "@faire/retailer-visitor-shared/components/Filters/ToggleSidebarFiltersPill";
import { useFilterScrollToProducts } from "@faire/retailer-visitor-shared/components/Filters/useFilterScrollToProducts";
import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { Filter as FilterIcon } from "@faire/slate/icons/Filter";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionCategoryOther/filterInteraction";
import { trackRetailersFilterClearClick } from "@faire/web-api--source/events/retailers/click/filterClear";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Button } from "@faire/web/slate/Button";
import { Pill } from "@faire/web/slate/Filter/styled";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";

interface IProps {
  name: FilterName;
  className?: string;
  isLoading?: boolean;
  // Wrapper component to wrap the children without breaking the sticky behavior
  Wrapper?: React.FC<React.PropsWithChildren>;
  // Whether to show the filter collapse pill on tablet and below during SSR
  shouldNotShowFilterCollapsePillOnTabletAndBelow?: boolean;
  shouldShowSort?: boolean;
  shouldShowCarouselArrows?: boolean;
  shouldEnableScrollToProducts?: boolean;
}

export const FilterInfoSticky: React.FC<IProps> = ({
  name,
  className,
  isLoading,
  Wrapper = React.Fragment,
  shouldNotShowFilterCollapsePillOnTabletAndBelow = false,
  shouldShowSort,
  shouldEnableScrollToProducts = false,
  shouldShowCarouselArrows = false,
}) => {
  useTraceComponentRender("FilterInfoSticky");

  const isSSR = useIsSSR();
  const { strictLocalize } = useStrictLocalization();
  const {
    data: { activeFilterCount, shouldShowFilterCollapsePill, filterSections },
    setFilterState,
    clearAllFilters,
  } = useFilterStore([
    "activeFilterCount",
    "shouldShowFilterCollapsePill",
    "filterSections",
  ]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useFilterScrollToProducts(
    scrollRef,
    !isLoading,
    shouldEnableScrollToProducts
  );

  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [isDragging, setIsDragging] = useState(false);
  const hasFilterSelected = activeFilterCount > 0;
  const onMobileAllFilterIconClick = () => {
    trackRetailersFilterInteraction(name, "open_filters", "modal");
    setFilterState((currentState) => ({
      ...currentState,
      isMobileModalOpen: true,
      isNewFilterSelected: false,
    }));
  };

  const handleClearFilters = () => {
    if (activeFilterCount > 0) {
      trackRetailersFilterClearClick(name, "mobile_clear_all", "");
      clearAllFilters();
    }
  };

  if ((isLoading === false && filterSections.length === 0) || isSSR) {
    return null;
  }

  return (
    <>
      {shouldEnableScrollToProducts ? <div ref={scrollRef} /> : null}
      <MaybeDraggableStickyRowForLoggedOutPage
        className={className}
        isLoading={isLoading}
        setIsDragging={setIsDragging}
        overflowVisible={shouldShowSort}
      >
        <Wrapper>
          {shouldShowFilterCollapsePill ? (
            <MaybeToggleSidebarFiltersPill
              shouldNotShowOnTabletAndBelow={
                shouldNotShowFilterCollapsePillOnTabletAndBelow
              }
            />
          ) : null}
          <MobileButtonsContainer>
            {hasFilterSelected ? (
              <StyledClearButton
                variant="tertiary"
                onClick={handleClearFilters}
              >
                <StrictLocalMsg
                  id="M+I5uz"
                  defaultMessage="Clear all"
                  description={{ text: "Remove all applied filters" }}
                />
              </StyledClearButton>
            ) : null}
            {activeFilterCount > -1 ? (
              <StyledPill
                isOpen={false}
                alwaysShowCloseButton={hasFilterSelected}
                hasFilterSelected={true}
                onClick={onMobileAllFilterIconClick}
                role="button"
                data-test-id="open-filters-modal-button"
                aria-label={strictLocalize({
                  id: "bA3RRW",
                  defaultMessage: "Open filters modal",
                  description: {
                    text: "accessible text to open the filters modal",
                  },
                })}
                tabIndex={0}
                onKeyDown={handleKeyDown(onMobileAllFilterIconClick)}
              >
                <Spacer width={4} />
                <Typography style={{ width: "max-content" }}>
                  {hasFilterSelected ? (
                    <StrictLocalMsg
                      id="JnXG/8"
                      defaultMessage="Filters ({activeFilterNumber})"
                      description={{
                        text: "Show the number of active filters to the user",
                      }}
                      values={{
                        activeFilterNumber: activeFilterCount,
                      }}
                    />
                  ) : (
                    <StrictLocalMsg
                      id="aUieV5"
                      defaultMessage="All filters"
                      description={{
                        text: "Narrow down the search (by applying filters)",
                      }}
                    />
                  )}
                </Typography>
                <Spacer width={4} />
                <FilterIcon fontSize="16px" />
              </StyledPill>
            ) : null}
            <Spacer width="1x" />
          </MobileButtonsContainer>
          <StyledToggleFilterRow
            name={name}
            $useUpdatedSpacing={isLoggedInRetailer}
            $isDragging={isDragging}
            $addPaddingRight={shouldShowSort}
            shouldShowCarouselArrows={shouldShowCarouselArrows}
          />
        </Wrapper>
        {shouldShowSort ? (
          <ResponsiveContentV2
            desktopAndAbove={<StyledSort name={name} roundCorners />}
          />
        ) : null}
      </MaybeDraggableStickyRowForLoggedOutPage>
    </>
  );
};

const StyledSort = styled(Sort)`
  margin-left: auto;
  padding-top: ${getSpacing("1x")};
  padding-bottom: ${getSpacing("1x")};
  max-height: 56px;
  ${Header} {
    &:hover {
      border-color: ${Core.border.default};
    }
  }
`;

const StyledToggleFilterRow = styled(ToggleFilterRow)<{
  $useUpdatedSpacing: boolean;
  $isDragging: boolean;
  $addPaddingRight?: boolean;
}>`
  ${({ $isDragging }) =>
    $isDragging
      ? css`
          user-select: none;
          pointer-events: none;
        `
      : null}
  padding: ${getSpacing("1x", 0)};
  ${({ $addPaddingRight }) =>
    $addPaddingRight
      ? css`
          padding-right: ${getSpacing("1x")};
        `
      : null}
  &:empty {
    display: none;
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${({ $useUpdatedSpacing }) =>
      $useUpdatedSpacing ? getSpacing("1x", 0) : undefined};
  }
`;

const StyledClearButton = styled(Button)`
  margin-right: ${getSpacing("2x")};
  min-width: max-content;
`;

const StyledPill = styled(Pill)`
  padding: ${getSpacing(0, 10)};
  border: 1px solid ${Core.border.muted};
  background-color: ${Core.surface.inverse};

  &:hover {
    border-color: ${Core.border.default};
    background-color: ${Core.surface.inverse};
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const MobileButtonsContainer = styled(Row)`
  min-width: max-content;
  padding: ${getSpacing("1x", 0)};

  &&& {
    ${responsiveMediaQueries.desktopAndAbove} {
      display: none;
    }
  }
`;

const MaybeToggleSidebarFiltersPill = ({
  shouldNotShowOnTabletAndBelow,
}: {
  shouldNotShowOnTabletAndBelow: boolean;
}) => {
  return shouldNotShowOnTabletAndBelow ? (
    <ResponsiveContentV2
      noJS={true}
      desktopAndAbove={<ToggleSidebarFiltersPill />}
    />
  ) : (
    <ToggleSidebarFiltersPill />
  );
};
